import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from "react";
import { invariant } from "../utils/invariant";
import * as tools from "../utils/tools";
import {
  linkUrl,
  LinkMode,
  Provider,
  searchProviders,
} from "../utils/searchProviders";
import { ensureType } from "@app/utils/ensureType";
import { AppItem } from "@app/types";

export type Sorting = keyof typeof sortFunctions;

type PreferencesContextDescriptor = {
  sorting: Sorting;
  sortings: Sorting[];
  sortingFunction: SortFunction;
  setSorting: (sorting: Sorting) => void;
  getLink: (item: string) => string;
  providers: Provider[];
  provider: Provider;
  setProvider: (provider: Provider) => void;
  itemLinkMode: LinkMode;
  setItemLinkMode: (itemLinkMode: LinkMode) => void;
};

const PreferencesContextValue =
  createContext<PreferencesContextDescriptor | null>(null);

export const PreferencesContext: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const [sorting, setSorting] = useState<Sorting>(() => {
    try {
      const storedSorting = localStorage.getItem("sorting");
      if (storedSorting && storedSorting in sortFunctions) {
        return storedSorting as Sorting;
      }
    } catch {}
    return "name";
  });
  const [provider, setProvider] = useState<Provider>(() => {
    try {
      const storedProvider = localStorage.getItem("provider");
      if (storedProvider && storedProvider in searchProviders) {
        return storedProvider as Provider;
      }
    } catch {}
    return "Google";
  });
  const [itemLinkMode, setItemLinkMode] = useState<LinkMode>(() => {
    try {
      const storedItemLinkMode = localStorage.getItem("itemLinkMode");
      if (
        storedItemLinkMode &&
        (storedItemLinkMode === "standard" || storedItemLinkMode === "hires")
      ) {
        return storedItemLinkMode as LinkMode;
      }
    } catch {}
    return "standard";
  });

  const contextValue = useMemo(
    () => ({
      sorting,
      sortings: Object.keys(sortFunctions) as Sorting[],
      sortingFunction: sortFunctions[sorting],
      // The sidebar's names are anchors somebody can middle-click into a real tab, so
      // this is the link URL and not the framed one — it used to carry `igu=1`, which
      // strips Google's page down for no reason outside the pane.
      getLink: (item: string) =>
        linkUrl(searchProviders[provider], itemLinkMode, item),
      setSorting: (sorting: Sorting) => {
        try {
          localStorage.setItem("sorting", sorting);
        } catch {}
        setSorting(sorting);
      },
      providers: Object.keys(searchProviders) as Provider[],
      provider,
      setProvider: (provider: Provider) => {
        try {
          localStorage.setItem("provider", provider);
        } catch {}
        setProvider(provider);
      },
      itemLinkMode,
      setItemLinkMode: (itemLinkMode: LinkMode) => {
        try {
          localStorage.setItem("itemLinkMode", itemLinkMode);
        } catch {}
        setItemLinkMode(itemLinkMode);
      },
    }),
    [sorting, setSorting, provider, setProvider, itemLinkMode, setItemLinkMode]
  );

  return (
    <PreferencesContextValue.Provider value={contextValue}>
      {children}
    </PreferencesContextValue.Provider>
  );
};

export const usePreferences = () =>
  useContext(PreferencesContextValue) ??
  invariant("PreferencesContext is missing");

type SortFunction = {
  (a: AppItem, b: AppItem): number;
  reset?: () => void;
};

const sortFunctions = {
  name: ensureType<SortFunction>((a, b) => a.name.localeCompare(b.name)),
  family: ensureType<SortFunction>((a, b) =>
    a.name
      .split(" ")
      .reverse()
      .join(" ")
      .localeCompare(b.name.split(" ").reverse().join(" "))
  ),
  rating: ensureType<SortFunction>((a, b) =>
    (a.rating ?? 0) < (b.rating ?? 0) ? 1 : -1
  ),
  date: ensureType<SortFunction>((a, b) => (a.addedAt < b.addedAt ? 1 : -1)),
  random: ensureType<SortFunction>(
    (() => {
      const seed = Math.floor(Date.now() / 1000 / 60 / 60 / 8);
      const rand = new tools.SRandom(seed);
      const fn = ensureType<SortFunction>(() => (Math.random() > 0.5 ? -1 : 1));
      fn.reset = () => rand.reset();
      return fn;
    })()
  ),
};
